import React, { useEffect, useState, useContext, useRef } from 'react';
import { getOrganizationalUnit } from '../services/api_org_unit';
import { getHolidaysCompanyList } from '../services/api_holiday';
import { AuthContext } from "../contexts/auth";
import TimeRangeSelect from './time_range_select';
import { getDayCalendarWorkingHour, getEmployeesWithActiveContract } from '../services/api_calendar';
import { Link } from 'wouter';
import Swal from 'sweetalert2';
import Fetching from './fetching';
import CalendarDaySelect from './calendar_day_select';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import NoPermissions from '../authorization/NoPermissions';
import ComboboxMultiSelectAPI from './combobox_multiselect_autocomplete_api';
import ListBoxPageSize from './listbox_pase_size';
import InOutsModal from './inouts_modal';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';


const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#ddd6fe',
    color: '#374151',
    fontSize: '0.8rem',
  },
});



const EmployeeAttendanceTable = ({ selectedUnit, page, pageSize, setTotalResult, setNextPage, setPrevPage }) => {
    const { loading, token, profile } = useContext(AuthContext);
    const [dipendenti, setDipendenti] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedTimeRange, setSelectedTimeRange] = useState('day');
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [isErrorEmployees, setIsErrorEmployees] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [unitData, setUnitData] = useState(null);
    const [employeeCalendarDailyWh, setEmployeeCalendarDailyWh] = useState(null);
    const [employeeShift, setEmployeeShift] = useState([]);
    const [allHolidays, setAllHolidays] = useState([])
    const [holidayToShow, setHolidayToShow] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [isForbidden, setIsForbidden] = useState(false);
    const [loadTime, setLoadTime] = useState(null);
    const [showInoutsModal, setShowInoutsModal] = useState(false);
    const [employeeDetails, setEmployeeDetails] = useState('');
    const hasFetched = useRef(false);
    const controller = useRef(null);
    const isEmployee = profile?.customer_type === 'employee';
    
    useEffect(() => {
        setLoadTime(moment());
    }, []);
    

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
    
        async function fetchData() {
            try {
                const holidaysResponse = await getHolidaysCompanyList(token, 'attendance');
                
                if (Array.isArray(holidaysResponse))
                    setAllHolidays(holidaysResponse);
               
                setIsLoading(false);
            } catch (error) {
                console.error('Errore festività:', error);
                if (error?.response?.status !== 402) {
                    setIsError(false);
                } else {
                    setIsError(true);
                    setIsLoading(false);
                }
            }
        }
    
        fetchData();
    }, [token]);
    
    
    useEffect(() => { 
        if (allHolidays.length === 0) return;
    
        const dateObj = new Date(selectedDate);
        const day = dateObj.getDate();
        const month = dateObj.getMonth() + 1;
    
        const filteredHoliday = allHolidays.filter(holiday => 
            holiday.day === day && holiday.month === month
        );
    
        setHolidayToShow(filteredHoliday);
    }, [selectedDate, allHolidays]);
    
    
    const options = { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedDate = new Intl.DateTimeFormat('it-IT', options).format(selectedDate);

    useEffect(() => {
        if(controller.current) {
            controller.current.abort();
            setIsLoadingEmployees(false);
        }
        
        controller.current = new AbortController();

        const signal = controller.current.signal;
    
        const getData = async () => {
            try {
                const today = moment(selectedDate).format('YYYY-MM-DD');
    
                setIsLoadingEmployees(true);
                setIsErrorEmployees(false);
                setIsForbidden(false);
    
                const calendarResponse = await getDayCalendarWorkingHour(
                    token,
                    today,
                    selectedUnit?.id,
                    selectedEmployees.map((e) => e.id),
                    page,
                    pageSize, 
                    signal,
                    'attendance'
                );
                
                let arrayCalendar = [];
                if (Array.isArray(calendarResponse.results)) {
                    arrayCalendar = calendarResponse.results;
                }

                setTotalResult(calendarResponse.count);
                setNextPage(calendarResponse.next);
                setPrevPage(calendarResponse.previous);
    
                const hasManageJustificationPermission =
                    profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
                    profile.employee?.permissions?.includes('hr.manage_justification_all');
    
                const hasViewTimbraturePermission =
                    profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
                    profile.employee?.permissions?.includes('hr.view_inouts_all') ||
                    profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
                    profile.employee?.permissions?.includes('hr.manage_inouts_all');
    
                const isEmployee = profile.customer_type === 'employee';
    
                const newDipendenti = [];
    
                const modifiedCalendarResponse = arrayCalendar?.map((entry) => {
                    newDipendenti.push({
                        first_name: entry.first_name,
                        last_name: entry.last_name,
                        cf: entry.cf,
                        id: entry.id,
                        has_active_contract: entry?.days[0]?.has_active_contract,
                    });
    
                    return {
                        ...entry,
                        days: entry.days.map((day) => ({
                            ...day,
                            justification_requests:
                                isEmployee && !hasManageJustificationPermission
                                    ? []
                                    : day.justification_requests,
                            inouts:
                                isEmployee && !hasViewTimbraturePermission
                                    ? []
                                    : day.inouts,
                            has_active_contract: day.has_active_contract,
                        })),
                    };
                });
                setDipendenti(newDipendenti);
    
                const employeeWorkingHoursData = modifiedCalendarResponse?.flatMap((entry) =>
                    entry.days.map((day) => ({
                        employeeId: entry.id,
                        date: day.date,
                        typology: entry.typology,
                        week_hours: day.week_hours,
                        expected_week_hours: day.expected_week_hours,
                        shift_type: day.shift_type,
                        has_active_contract: day.has_active_contract,
                        holidays: day.holidays,
                        justification_requests:
                            isEmployee && !hasManageJustificationPermission
                            ? []
                            : (day.justification_requests || []).map((justification) => ({
                                datetime_from: justification.datetime_from,
                                datetime_to: justification.datetime_to,
                                description: justification.description,
                                })),
                        inouts:
                            isEmployee && !hasViewTimbraturePermission
                            ? []
                            : day.inouts,
                        shifts: (day.shifts || []).map((shift) => ({
                            ref_id: shift.ref_id,
                            ref_name: shift.ref_name,
                            ref_ou: shift.ref_ou,
                            daily_working_hour: shift.daily_working_hour,
                            time_slots: (shift.time_slots || []).map((slot) => ({
                            ...slot,
                            date: day.date,
                            })),
                        })),
                    }))
                );
    
                setEmployeeCalendarDailyWh(modifiedCalendarResponse);
                setEmployeeShift(employeeWorkingHoursData);
                setIsLoadingEmployees(false);
            } catch (error) {
                if (error.response && error?.response?.status === 403) {
                    setIsForbidden(true);
                } else if (error?.name !== 'CanceledError') {
                    console.error(error);
                    setIsErrorEmployees(true);
                    setIsLoadingEmployees(false);
                } else if (error?.response?.status !== 402) {
                    setIsErrorEmployees(false);
                }
            }
        }

        getData();

    }, [token, selectedDate, page, pageSize, selectedUnit, selectedEmployees]);

    const handlePageSizeChange = (newPageSize) => {
        // setPageSize(newPageSize); 
        setPage(1); 
    };

    useEffect(() => {
        if (!selectedUnit) {
            setSelectedEmployees([]);
        }
    }, [selectedUnit]);

    useEffect(() => {
        if (selectedUnit && selectedEmployees.length > 0) {
            setSelectedEmployees([]);
        }
    }, [selectedUnit]);

    const handleSelectionChange = (newSelectedEmployees) => {
        setSelectedEmployees(newSelectedEmployees);
    };

    const handleDeselectEmployee = (employeeId) => {
        setSelectedEmployees((prevSelected) =>
            prevSelected.filter((emp) => emp.id !== employeeId)
        );
    };

    const handleResetSelection = () => {
        setSelectedEmployees([]);
    };

    const handleDaySelect = (date) => {
        const selectedDay = moment(date).startOf('day').toDate();
        setSelectedDate(selectedDay);
        setCalendarVisible(!calendarVisible);
    };
    

    const handleSelectClick = () => {
        setCalendarVisible(!calendarVisible);
    };

    const handlePreviousDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() - 1);
        setSelectedDate(newDate);
    };

    const handleNextDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() + 1);
        setSelectedDate(newDate);
    };


    const getMinuteColor = (employeeId, hour, minute) => {
        if (!employeeCalendarDailyWh) {
            return 'transparent';
        }
    
        const selectedDay = moment(selectedDate).format('YYYY-MM-DD');
        const employeeShift = employeeCalendarDailyWh.find(
            (entry) => entry.id === employeeId && entry.days.some((day) => day.date === selectedDay)
        );
        if (!employeeShift) {
            return 'transparent';
        }
    
        const dayShifts = employeeShift.days.find((day) => day.date === selectedDay);
        if (!dayShifts) {
            return 'transparent';
        }
    
        const [currentHour] = hour.split(':').map(Number);
        const currentMinute = minute;
        
        if (dayShifts.justification_requests?.some((justification) => {
 
            const currentTime = moment(selectedDate).startOf('day').add(currentHour, 'hours').add(currentMinute, 'minutes');
            
            const justificationStart = moment(justification.datetime_from);
            const justificationEnd = moment(justification.datetime_to);
            
            return currentTime.isBetween(justificationStart, justificationEnd, null, '[)');

        })){
            return 'bg-orange-400';
        }
    
        for (const shift of dayShifts.shifts || []) {
            for (const slot of shift.time_slots || []) {
                const shiftStart = new Date(`1970-01-01T${slot.start_at}:00`);
                const shiftEnd = new Date(`1970-01-01T${slot.end_at === '24:00' ? '23:59:59' : `${slot.end_at}:00`}`);
    
                const startHour = shiftStart.getHours();
                const startMinute = shiftStart.getMinutes();
                const endHour = shiftEnd.getHours();
                const endMinute = shiftEnd.getMinutes();
    
                if ((currentHour > startHour || (currentHour === startHour && currentMinute >= startMinute)) &&
                    (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute))) {
                    
                    if (shift?.status === false) {
                        return 'bg-sky-200';
                    }

                    for (const pause of slot.breaks || []) {
                        const pauseStart = new Date(`1970-01-01T${pause.start_at}:00`);
                        const pauseEnd = new Date(`1970-01-01T${pause.end_at}:00`);
    
                        const pauseStartHour = pauseStart.getHours();
                        const pauseStartMinute = pauseStart.getMinutes();
                        const pauseEndHour = pauseEnd.getHours();
                        const pauseEndMinute = pauseEnd.getMinutes();
    
                        if ((currentHour > pauseStartHour ||
                                (currentHour === pauseStartHour && currentMinute >= pauseStartMinute)) &&
                            (currentHour < pauseEndHour ||
                                (currentHour === pauseEndHour && currentMinute < pauseEndMinute))) {
                            return 'bg-gray-300';
                        }
                    }
    
                    return 'bg-indigo-200';
                }
            }
        }
    
        return 'transparent';
    };
    
    const getInoutColor = (employeeId, hour, minute) => {
        if (!employeeCalendarDailyWh) {
            return 'transparent';
        }
    
        const selectedDay = moment(selectedDate).format('YYYY-MM-DD');
        const today = moment().format('YYYY-MM-DD');
        const currentHour = moment().hour();
        const currentMinute = moment().minute();

    
        const employeeShift = employeeCalendarDailyWh.find(
            (entry) => entry.id === employeeId && entry.days.some((day) => day.date === selectedDay)
        );
    
        if (!employeeShift) {
            return 'transparent';
        }
    
        const dayData = employeeShift.days.find((day) => day.date === selectedDay);
        if (!dayData || !dayData.inouts) {
            return 'transparent';
        }
       
        const checkThresholdsIN = dayData?.inout?.over_time_checkin;
        const checkThresholdsOUT = dayData?.inout?.over_time_checkout;
        // const errorColor = '#f87171';
        const errorColor = 'bg-red-400';
        
        // const barColor = '#34d399';
        const barColor = 'bg-green-500';
        
        const [cellHour] = hour.split(':').map(Number);
        const cellMinute = minute;

        const ongoingCheckin = dayData.inouts.find((inout) => {
            const checkin = new Date(inout.checkin);
            const checkout = inout.checkout ? new Date(inout.checkout) : null;
    
            if (!checkout && moment(selectedDate).isSameOrAfter(moment(checkin), 'day')) {
                return true;
            }
    
            return false;
        });

        for (let inout of dayData.inouts) {
            const checkin = inout.checkin ? new Date(inout.checkin) : null;
            const checkout = inout.checkout ? new Date(inout.checkout) : null;
        
            const cellMoment = moment(`${selectedDay} ${cellHour}:${cellMinute}`, 'YYYY-MM-DD HH:mm');
        
            if (checkin && checkout) {
                const checkinMoment = moment(checkin);
                const checkoutMoment = moment(checkout);
        
                if (cellMoment.isBetween(checkinMoment, checkoutMoment, 'minute', '[]')) {
                    return (inout.over_time_checkin || inout.over_time_checkout) ? errorColor : barColor;
                }
            }
        
            if (checkin && !checkout) {
                const checkinMoment = moment(checkin);
        
                if (
                    moment(selectedDate).isSameOrBefore(today, 'day') &&
                    cellMoment.isSameOrAfter(checkinMoment)
                ) {
                    const isToday = selectedDay === today;
                    const now = moment();
        
                    if (!isToday || cellMoment.isSameOrBefore(now)) {
                        return (inout.over_time_checkin || inout.over_time_checkout) ? errorColor : barColor;
                    }
                }
            }
        
            if (!checkin && checkout) {
                const checkoutMoment = moment(checkout);
        
                if (cellMoment.isSameOrBefore(checkoutMoment)) {
                    return (inout.over_time_checkin || inout.over_time_checkout) ? errorColor : barColor;
                }
            }
        }
    
        if (ongoingCheckin) {
            const checkinTime = new Date(ongoingCheckin.checkin);            
            const currentTime = moment(selectedDate).startOf('day').add(cellHour, 'hours').add(cellMinute, 'minutes');
            
            const checkInTime = moment(ongoingCheckin.checkin);
            const fakeCheckOutTime = moment();

            if (currentTime.isBetween(checkInTime, fakeCheckOutTime, null, '[]')) {
                if(checkThresholdsIN || checkThresholdsOUT)
                    return errorColor;

                return barColor;
            }
        }
    
        const isWithinInouts = dayData.inouts.some((inout) => {
            const checkin = new Date(inout.checkin);
            const checkout = inout.checkout ? new Date(inout.checkout) : null;
    
            const checkinHour = checkin.getHours();
            const checkinMinute = checkin.getMinutes();
    
            // If checkout is absent
            if (!checkout) {
                if (moment(selectedDate).isBefore(today, 'day')) {
                    return (
                        moment(selectedDate).isAfter(moment(checkin), 'day') ||
                        (moment(selectedDate).isSame(moment(checkin), 'day') &&
                            (cellHour > checkinHour || (cellHour === checkinHour && cellMinute >= checkinMinute)))
                    );
                }
                // Check if we're examining the current day and if the checkin is in the past
                if (moment(selectedDate).isSame(moment(checkin), 'day')) {
                    // If the checkin is today and there's no checkout
                    if (selectedDay === today) {
                        // Highlight from 00:00 until current time
                        return (
                            (cellHour > checkinHour || (cellHour === checkinHour && cellMinute >= checkinMinute)) &&
                            (cellHour < currentHour || (cellHour === currentHour && cellMinute <= currentMinute))
                        );
                    }
                    // Highlight from checkin to the end of the selected day
                    return (
                        (cellHour > checkinHour || (cellHour === checkinHour && cellMinute >= checkinMinute)) &&
                        (cellHour <= 23 || (cellHour === 23 && cellMinute <= 59))
                    );
                } else if (moment(selectedDate).isAfter(moment(checkin), 'day')) {
                    // If the selected day is after the checkin day and there's no checkout
                    return (
                        cellHour < currentHour
                        || (cellHour === currentHour && cellMinute <= currentMinute)
                    );
                }
            } 
            else if (moment(selectedDay).isBetween(checkin, checkout, 'days', '()')) {
                return true;
            }
             else {                    
                const cellMoment = moment(`${selectedDay} ${cellHour}:${cellMinute}`, 'YYYY-MM-DD HH:mm');
                return cellMoment.isBetween(checkin, checkout, 'minute', '[]');
            }
    
            // If checkout is absent and the selected date is in the future, do not show the color
            if (!checkout && moment(selectedDate).isAfter(today)) {
                return false;
            }
    
            return false;
        });

        if (!isWithinInouts)
            return 'transparent';

        if (checkThresholdsIN || checkThresholdsOUT)
            return errorColor;

        return barColor;
    };

    const handleSwalMessage = (employeeId, hour, minute) => {
        if (!employeeCalendarDailyWh) {
            return;
        }
    
        if (!hour || !minute) {
            return;
        }
    
        const selectedDay = moment(selectedDate).format('YYYY-MM-DD');
        const employeeShift = employeeCalendarDailyWh.find((entry) => entry.id === employeeId && entry.days.some((day) => day.date === selectedDay));
    
        if (!employeeShift) {
            return;
        }
    
        const dayShifts = employeeShift.days.find((day) => day.date === selectedDay);
        if (!dayShifts) {
            return;
        }
        
        const allJustifications = (dayShifts.justification_requests ?? []).map((justification) => {
            const justificationFrom = moment(justification.datetime_from);
            const justificationTo = moment(justification.datetime_to);

            if (justificationTo.format('HH:mm') === '00:00') {
                justificationTo.subtract(1, 'minute');
            }
    
            const startHour = justificationFrom.hour();
            const startMinute = justificationFrom.minute();
            const endHour = justificationTo.hour();
            const endMinute = justificationTo.minute();
    
            return {
                ...justification,
                startHour,
                startMinute,
                endHour,
                endMinute,
                justificationFrom,
                justificationTo,
            };
        });
    
        allJustifications.sort((a, b) => {
            if (a.startHour !== b.startHour) {
                return a.startHour - b.startHour;
            }
            return a.startMinute - b.startMinute;
        });
    
        const [currentHour] = hour.split(':').map(Number);
    
        const isInTimeSlot = dayShifts.time_slots.some((shift) => {
            const shiftStart = moment(`1970-01-01T${shift.start_at}:00`);
            const shiftEnd = moment(`1970-01-01T${shift.end_at === '24:00' ? '23:59' : shift.end_at}:00`);
    
            const startHour = shiftStart.hour();
            const startMinute = shiftStart.minute();
            const endHour = shiftEnd.hour();
            const endMinute = shiftEnd.minute();
    
            return (currentHour > startHour || (currentHour === startHour && minute >= startMinute)) &&
                (currentHour < endHour || (currentHour === endHour && minute <= endMinute));
        });
    
        const isInJustifications = allJustifications.some((justification) => {
            return (currentHour > justification.startHour || (currentHour === justification.startHour && minute >= justification.startMinute)) &&
                (currentHour < justification.endHour || (currentHour === justification.endHour && minute <= justification.endMinute));
        });
    
        if (!isInTimeSlot && !isInJustifications) {
            return;
        }
    
        let htmlContent = `
            <div>
                ${dayShifts.time_slots.map((shift) => {
                    const shiftStart = moment(`1970-01-01T${shift.start_at}:00`);
                    const shiftEnd = moment(`1970-01-01T${shift.end_at}:00`);
    
                    return `
                        <p style="padding-bottom: 8px;">
                            <strong>Fascia oraria lavoro:</strong> ${shift.start_at} - ${shift.end_at}
                        </p>
                        ${shift.breaks && shift.breaks.length > 0 ? `
                            <p style="padding-top: 2px; color: #9ca3af;"><strong>Pause</strong></p>
                            <ul style="padding-bottom: 8px;">
                                ${shift.breaks.map((breakSlot) => `
                                    <li>${breakSlot.start_at} - ${breakSlot.end_at}</li>
                                `).join('')}
                            </ul>
                            <hr style="border-top: 1px solid #ccc; margin-bottom: 8px;">
                        ` : ''}
                    `;
                }).join('')}
        
                ${allJustifications.length > 0 ? `
                    <p style="padding-top: 10px; color: #FB9123;"><strong>Giustificativi:</strong></p>
                    <ul>
                        ${allJustifications.map((justification) => {
                            const justificationFromFormatted = justification.justificationFrom.format('HH:mm');
                            const justificationToFormatted = justification.justificationTo.format('HH:mm');
                            const justificationDateFrom = justification.justificationFrom.format('DD/MM');
                            const justificationDateTo = justification.justificationTo.format('DD/MM');
    
                            // Condizione per "Intera giornata"
                            if (justificationFromFormatted === '00:00' && justificationToFormatted === '23:59') {
                                return `<li>${justification.justification.justification.description}: Intera giornata</li>`;
                            }
    
                            // Condizione per giorni diversi
                            if (justificationDateFrom !== justificationDateTo) {
                                return `<li>${justification.justification.justification.description}: dalle ore ${justificationFromFormatted} del ${justificationDateFrom} alle ore ${justificationToFormatted} del ${justificationDateTo}</li>`;
                            }
    
                            // Default: mostra orari e data (per giustificativi nello stesso giorno)
                            return `<li>${justification.justification.justification.description}: dalle ore ${justificationFromFormatted} alle ore ${justificationToFormatted}</li>`;
                        }).join('')}
                    </ul>
                ` : ''}
            </div>
        `;
    
        Swal.fire({
            title: "Dettagli del Turno",
            html: htmlContent,
        });
    };

    const handleSwalInOutsDetails = (employeeDetails, locations) => {
        if (!employeeCalendarDailyWh) {
            return;
        }
        setEmployeeDetails(employeeDetails)
        setShowInoutsModal(locations);
    };   
     

    if (isForbidden) {
        return <NoPermissions />;
    }
    
    if(isLoadingEmployees)
        return (
        <div className='w-full'>
            <Fetching />
        </div>
        );
    if (isErrorEmployees) return <div>Error loading data employee</div>;

    if (isLoading) return (
        <div className='w-full'>
            <Fetching />
        </div>
        );
    if (isError) return <div>Error loading data</div>;
    
    const dayTimeHours = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'];
    const nightTimeHours = ['22:00', '23:00', '00:00', '01:00', '02:00', '03:00', '04:00', '05:00'];
    const filteredHours = selectedTimeRange === 'day' ? dayTimeHours : selectedTimeRange === 'night' ? nightTimeHours : ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'];
    
    const hasManageJustificationPermission =
        profile.employee?.permissions.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions.includes('hr.manage_justification_all');
    
    const hasViewTimbraturePermission =
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');


    return (
        <>
            <div className='container mx-4 bg-white p-3 rounded-md'>
                <div
                    style={{ flex: '1 1 auto', width: 'auto' }}
                    className="py-5 px-4 border-x border-t border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
                >
                    <div className='grid md:grid-cols-5 sm:justify-start md:justify-around grid-cols-3'>
                        <div className='col-span-1 p-2 flex align-center justify-end sm:justify-center md:justify-end me-3'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                onClick={handlePreviousDay}
                            >
                                <ChevronLeftIcon className="w-5 h-5 cursor-pointer" />
                            </button>
                        </div>
                        <div className='col-span-1 p-2 flex align-center md:justify-end sm:justify-center me-3'>Seleziona giorno</div>
                        <div className='col-span-1 p-2 align-center justify-start sm:justify-center md:justify-start md:hidden block'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                onClick={handleNextDay}
                            >
                                <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                            </button>
                        </div>
                        <div
                            onClick={handleSelectClick}
                            style={{ cursor: 'pointer' }}
                            className='col-span-3 md:col-span-2 border border-gray-300 rounded-md shadow-md p-2 pl-4 flex items-center md:w-4/5 w-full justify-between'
                        >
                            <div>
                                {selectedDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                            {!calendarVisible ? <ChevronDownIcon className='h-4 w-4 bg-indigo-100 rounded-md' /> : <ChevronUpIcon className='h-4 w-4 bg-indigo-100 rounded-md' />}
                        </div>
                        <div className='col-span-1 p-2 align-start justify-start hidden md:block'>
                            <button
                                className='text-gray-700 font-semibold px-1 hover:bg-gray-100 flex items-start'
                                onClick={handleNextDay}
                            >
                                <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                            </button>
                        </div>
                    </div>
                    {calendarVisible &&         
                        <CalendarDaySelect
                            onDaySelect={handleDaySelect}
                            onCloseCalendar={() => {}}
                            selectedMonth={selectedDate}
                        />
                    }
                </div>
                <div className="grid sm:grid-cols-2 grid-cols-1 justify-between border-x border-gray-200 px-3">
                    <div className="col col-span-2 justify-start flex">
                        <h1 className='py-2'>PRESENZE GIORNO  
                            <span className='font-bold text-indigo-800 uppercase'> {formattedDate}</span>
                        </h1>
                    </div>
                    <div className="col col-span-2 justify-start flex">
                        {holidayToShow.length > 0 && (
                            <h1 className="uppercase">
                                festività oggi:  
                                {holidayToShow.map((holiday, index) => (
                                    <span key={index} className="font-bold text-indigo-800 uppercase ml-2">
                                        {holiday.name}
                                        {index < holidayToShow.length - 1 && ', '}
                                    </span>
                                ))}
                            </h1>
                        )}
                    </div>
                    <div className="col col-span-1 col-start-2 justify-end flex" style={{ zIndex: '50'}}>
                        <div className="flex mt-1 items-center justify-start">
                            <label id="label-timeRangeSelect" className="font-medium text-gray-700 uppercase text-sm mr-2 whitespace-nowrap">Seleziona orario:</label>
                            <TimeRangeSelect aria-labelledby="label-timeRangeSelect" selectedTimeRange={selectedTimeRange} handleTimeRangeChange={setSelectedTimeRange} />
                        </div>
                    </div>
                </div>
       
                <div className='px-3 py-3 w-full bg-white border-x border-gray-200' style={{ position: 'relative', zIndex: '20'}}> 
                    <ListBoxPageSize value={pageSize} onChange={handlePageSizeChange} />
                </div>
                
                <div className="grid sm:grid-cols-2 grid-cols-1 justify-between border-x border-gray-200 px-3">
                    <div className="col col-span-1 pb-4 justify-start flex z-10">
                        <div className="flex mt-1 items-center">
                            <label id="label-selectedEmployees" className="font-medium text-gray-700 uppercase text-sm mr-2 whitespace-nowrap">Seleziona dipendente:</label>
                            <ComboboxMultiSelectAPI
                                aria-labelledby="label-selectedEmployees"
                                getData={async (query) => {
                                    if (query.trim() === '') {
                                    return Promise.resolve([]);
                                    }

                                    const page = 1;
                                    const pageSize = 5;

                                    try {
                                    const res = await getEmployeesWithActiveContract(token, query, 'hr_active', null, page, pageSize, selectedUnit?.id);

                                    const employeesArray = Array.isArray(res.results) ? res.results : [];
                                        return employeesArray; 
                                    } catch (error) {
                                        console.error("Errore durante il recupero dei dati:", error);
                                    return [];
                                    }
                                }}
                                getDisplay={(dip) => dip.last_name && dip.first_name ? `${dip.last_name} ${dip.first_name}` : dip.cf}
                                getId={(dip) => dip.id}
                                selectedValues={selectedEmployees || selectedUnit}
                                onChange={handleSelectionChange}
                                placeholder="Cerca dipendente..."
                            />
                        </div>
                    </div>
                </div>
                {selectedEmployees.length > 0 ? (
                    <div className="border-x border-gray-200 py-2">
                        <div className="flex flex-wrap gap-2">
                            {selectedEmployees.map((employee) => (
                                <div
                                    key={employee.id}
                                    className="ml-2 inline-flex items-center px-3 py-1 rounded-full bg-indigo-200 text-indigo-800 text-xs font-medium cursor-pointer hover:bg-indigo-300"
                                    onClick={() => handleDeselectEmployee(employee.id)}
                                >
                                    {employee?.last_name && employee?.first_name
                                        ? `${employee?.last_name} ${employee?.first_name}`
                                        : employee?.cf}
                                    <span className="ml-2 text-gray-600 hover:text-red-500">&times;</span>
                                </div>
                            ))}
                            <button
                                className="ml-2 inline-flex items-center rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                onClick={handleResetSelection}
                            >
                                Reset selezione
                            </button>
                        </div>
                    </div>
                ) : null}
                <div className="flex items-center pt-4 pb-3 border-x border-gray-200 px-3">
                    <div className="w-3 h-3 bg-indigo-300 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Orario lavorativo</span>
                    <div className="w-3 h-3 bg-gray-300 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>pausa</span>
                    {!(profile.customer_type === 'employee' && !hasManageJustificationPermission) && (
                        <>
                            <div className="w-3 h-3 bg-orange-400 rounded-full mr-1"></div>
                            <span className='block font-medium text-gray-700 uppercase text-xs'>giustificativi</span>
                        </>
                    )}
                    {!(profile.customer_type === 'employee' && !hasManageJustificationPermission) && (
                        <>
                            <div className="w-3 h-3 bg-sky-200 rounded-full ml-2 mr-1"></div>
                            <span className='block font-medium text-gray-700 uppercase text-xs'>turni da confermare</span>
                        </>
                    )}
                </div>
                <div className="flex items-center pb-3 border-x border-gray-200 px-3">
                    {!(profile.customer_type === 'employee' && !hasViewTimbraturePermission) && (
                        <>
                            <div
                                className="w-3 h-3 rounded-full mr-1"
                                style={{
                                    background: 'linear-gradient(to right, #10B981 50%, #F87171 50%)'
                                }}
                            ></div>
                            <span className='block font-medium text-gray-700 uppercase text-xs'>timbratura</span> 
                            {loadTime && (
                                <span className='block text-gray-600 text-sm'> - aggiornata alle ore <span className='text-gray-800 font-semibold'>{loadTime.format('HH:mm:ss')}</span> del  <span className='text-gray-800 font-semibold'>{loadTime.format('DD/MM/YYYY')}</span></span>
                            )}
                        </>
                    )}
                </div>

                <div className="grid grid-cols-1">
                    <div className="overflow-x-auto" style={{ maxHeight: "40rem", overflowY: "scroll" }}>
                    {isLoading ? (
                        <div className='w-full'>
                            <Fetching />
                        </div>
                        ) : (
                            <div className='container overflow-auto' style={{ position: 'relative', maxHeight: '100%'}}>
                                <table className="min-w-max border divide-y divide-gray-200 w-full">
                                    <thead >
                                        <tr>
                                            <th className="pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"  style={{ position: 'sticky', top: 0, left: 0, zIndex: '9' }}>Dipendenti</th>
                                            {filteredHours.map(hour => (
                                                <th key={hour} className="py-3 sticky top-0 px-0.5 border-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase"
                                                style={{ zIndex: '5' }}>{hour}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {(() => {
                                            const employeesToShow = selectedEmployees.length > 0
                                                ? selectedEmployees
                                                : dipendenti;

                                            if (!isLoadingEmployees && employeesToShow.length === 0) {
                                                return (
                                                    <tr>
                                                        <td className="pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase" colSpan={filteredHours.length + 1}>Non sono presenti anagrafiche</td>
                                                    </tr>
                                                );
                                            }

                                            return employeesToShow
                                                .slice()
                                                .sort((a, b) => {
                                                    const aValue = a.last_name || a.cf;
                                                    const bValue = b.last_name || b.cf;
                                                    return aValue.localeCompare(bValue);
                                                })
                                                .map((dipendente, index) => {
                                                    const dayData = (employeeCalendarDailyWh?.find((employee) => employee.id === dipendente.id)?.days || [])[0];
                                                                                                        
                                                    return <tr key={dipendente.id} className={index % 2 === 1 ? 'bg-gray-100' : ''}>
                                                        <td
                                                            className="sticky left-0 pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase" style={{ zIndex: '7' }}
                                                            title={dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                                                        >
                                                            <Link href={`/dipendenti/${dipendente.cf}`} className="hover:text-indigo-600">
                                                                {dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}`.slice(0, 16) : dipendente.cf}
                                                                {dipendente.first_name && `${dipendente.last_name} ${dipendente.first_name}`.length > 16 ? '...' : ''}
                                                            </Link>
                                                        </td>
                                                        
                                                        {filteredHours.map((hour) => (
                                                            <td key={hour} className="border border-gray-200 text-center p-0 relative">
                                                                {dayData?.has_active_contract ? (
                                                                    <div className="relative w-full h-8 flex flex-col">
                                                                        {/* Orario di lavoro */}
                                                                        <div className="w-full h-4 flex mt-1">
                                                                            {Array.from({ length: 60 }, (_, minute) => (
                                                                                <div
                                                                                    key={minute}
                                                                                    className={`cursor-pointer ${getMinuteColor(dipendente.id, hour.toString(), minute)}`}
                                                                                    style={{ width: '1.66%', height: '24px' }}
                                                                                    title={`${hour}:${minute < 10 ? `0${minute}` : minute}`}
                                                                                    onClick={() => handleSwalInOutsDetails(dipendente, dayData)}
                                                                                ></div>
                                                                            ))}
                                                                        </div>

                                                                        {/* Timbrature */}
                                                                        {!(profile.customer_type === 'employee' && !hasViewTimbraturePermission) && (
                                                                            <div className="absolute w-full h-2 flex top-[14px] -translate-y-1/2">
                                                                                {Array.from({ length: 60 }, (_, minute) => (
                                                                                    <CustomTooltip
                                                                                        key={minute}
                                                                                        title={`${hour.toString().slice(0, 2)}:${minute < 10 ? `0${minute}` : minute}`}
                                                                                        placement="top-start"
                                                                                    >
                                                                                        <div
                                                                                            key={minute}
                                                                                            className={`cursor-pointer ${getInoutColor(dipendente.id, hour.toString(), minute)}`}
                                                                                            style={{ width: '1.66%', height: '12px' }}
                                                                                            onClick={() => handleSwalInOutsDetails(dipendente, dayData)}
                                                                                        ></div>
                                                                                    </CustomTooltip>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="bg-indigo-500 w-full text-transparent"
                                                                        style={{
                                                                            ...(PatternInactiveEmployee()),
                                                                        }}
                                                                    >
                                                                        .
                                                                    </div>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <InOutsModal locations={showInoutsModal} onClose={() => setShowInoutsModal(false)} isOpen={showInoutsModal} checkPermissionTimbratura={hasViewTimbraturePermission} isEmployee={isEmployee} employeeDetails={employeeDetails} />
            </div>
        </>
    );
};

export default EmployeeAttendanceTable;

 
// const PatternInactiveEmployee = () => {
//     return {
//         backgroundColor: '#ffffff',
//         backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='12' viewBox='0 0 40 12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 6.172L6.172 0h5.656L0 11.828V6.172zm40 5.656L28.172 0h5.656L40 6.172v5.656zM6.172 12l12-12h3.656l12 12h-5.656L20 3.828 11.828 12H6.172zm12 0L20 10.172 21.828 12h-3.656z' fill='%230891b2' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
//         backgroundRepeat: 'repeat',
//     };
// };

const PatternInactiveEmployee = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};
